import React from 'react';
import styled from 'styled-components';
import StarRating from './StarRating';
import { IRatingDistribution } from '../../section/Common/utils/ratingDistribution';
import { formatNumberUS } from '../../section/Common/utils/stringUtils';
import {
  AVERAGE_RATING_LABEL,
  RATING_ACCENT_COLOR,
  RATING_BREAKDOWN_UNAVAILABLE,
  RATING_TRACK_COLOR,
  getRatingsLabel
} from './constants';

interface IRatingBreakdownProps {
  rating: number;
  distribution: IRatingDistribution | null;
}

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-size: 14px;
  font-weight: 600;
  line-height: 22px;
  color: #262626;
`;

const Divider = styled.div`
  margin: 10px 0;
  border-top: 1px solid ${RATING_TRACK_COLOR};
`;

const Row = styled.div`
  display: grid;
  grid-template-columns: 10px 1fr 34px;
  align-items: center;
  gap: 10px;
  font-size: 12px;
  line-height: 20px;
  color: #595959;
`;

const Track = styled.div`
  height: 8px;
  border-radius: 8px;
  background: ${RATING_TRACK_COLOR};
`;

const Fill = styled.div<{ percentage: number }>`
  height: 100%;
  border-radius: 8px;
  background: ${RATING_ACCENT_COLOR};
  width: ${({ percentage }) => percentage}%;
`;

const Percentage = styled.span`
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const Unavailable = styled.div`
  padding: 8px 0;
  font-size: 12px;
  color: #595959;
  text-align: center;
`;

// Click bubbling is handled by RatingDistribution's popover boundary.
const RatingBreakdown: React.FC<IRatingBreakdownProps> = ({ rating, distribution }) => (
  <div data-testid='rating-breakdown'>
    <Header>
      <span>{`${rating} ${AVERAGE_RATING_LABEL}`}</span>
      <StarRating rating={rating} />
    </Header>

    <Divider />

    {distribution ? (
      distribution.buckets.map(({ star, count, percentage }) => (
        <Row
          key={star}
          title={getRatingsLabel(formatNumberUS(count))}
        >
          <span>{star}</span>
          <Track>
            <Fill percentage={percentage} />
          </Track>
          <Percentage>{percentage}%</Percentage>
        </Row>
      ))
    ) : (
      <Unavailable data-testid='rating-breakdown-unavailable'>
        {RATING_BREAKDOWN_UNAVAILABLE}
      </Unavailable>
    )}
  </div>
);

export default RatingBreakdown;
